import React, { useState, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DefaultLandingMessage, TableComponent } from 'components';
import { LINKS } from 'components/constants';
import { CategoryPopupForm, ConfirmationDialog } from 'components/Dialogs';
import { DEFAULT_TABLE_PARAMS } from 'components/TableComponent/constants';
import { useBusyProgressActions, useBusyProgressState } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import PageWithHeader from 'layouts/PageWithHeader/PageWithHeader';
import { categoriesModel } from 'models';
import { ActionType } from 'types/enums';
import { CategoryItem, TableParams, TableRow } from 'types/other';
import TEST_IDS from 'constants/testIds';

export function ManageCategories() {
  const { t } = useTranslation();
  const { isBusy } = useBusyProgressState();
  const { withPageProgressHandler } = useBusyProgressActions();
  const { handleServerError, resetServerErrors } = useServerErrorActions();

  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({ ...DEFAULT_TABLE_PARAMS });
  const [paginationItemsCount, setPaginationItemsCount] = useState(0);
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryItem>();
  const [categoryToEdit, setCategoryToEdit] = useState<CategoryItem | {}>();

  useEffect(() => {
    getCategories();
  }, [tableParams]);

  const getCategories = withPageProgressHandler(() => {
    const { search, ...rest } = tableParams;

    return categoriesModel
      .getCategories({ ...rest, name: search, withChildren: true })
      .then(data => {
        setCategories(data.categories);
        setPaginationItemsCount(data.categoriesCount);
      })
      .catch(handleServerError);
  });

  const handleDelete = withPageProgressHandler(({ id }) => {
    setCategoryToDelete(undefined);
    return categoriesModel.deleteCategory(id).then(getCategories).catch(handleServerError);
  });

  const handleCreateCategory = () => setCategoryToEdit({});

  const handleClose = () => {
    setCategoryToEdit(undefined);
    resetServerErrors();
  };

  const afterAction = () => {
    setCategoryToEdit(undefined);
    getCategories();
    handleClose();
  };

  const handleCreateSubcategory = (category: CategoryItem) => {
    setCategoryToEdit({ parentId: category.id });
  };

  const handleEditCategory = (currentCategory: CategoryItem) => {
    setCategoryToEdit(currentCategory);
  };

  const createRow = (category: CategoryItem): TableRow => ({
    cells: [category.name, category.productCount, category.subcategoriesCount, category.updatedAt],
    actions: [
      {
        type: category.level < 3 ? ActionType.ADD : ActionType.NONE,
        onClick: () => handleCreateSubcategory(category),
        testId: TEST_IDS.manageCategories.list.buttons.addSubcategory,
      },
      {
        type: ActionType.EDIT,
        onClick: () => handleEditCategory(category),
        testId: TEST_IDS.manageCategories.list.buttons.edit,
      },
      {
        type: ActionType.DELETE,
        onClick: () => setCategoryToDelete(category),
        testId: TEST_IDS.manageCategories.list.buttons.delete,
      },
    ],
    children: category.categories.map(createRow),
  });

  const tableData = useMemo(
    () => ({
      headers: [
        t('components.tableComponent.headers.email'),
        t('components.tableComponent.headers.itemsCount'),
        t('components.tableComponent.headers.subcategories'),
        t('components.tableComponent.headers.updated'),
      ],
      rows: categories.map(createRow),
    }),
    [categories],
  );

  return (
    <PageWithHeader
      tabTitle={t('pages.categories.title')}
      headerText={`${t('pages.categories.title')}: ${paginationItemsCount}`}
      button={{
        text: t('pages.categories.addCategory'),
        onClick: () => handleCreateCategory(),
      }}
    >
      <TableComponent
        data={tableData}
        count={paginationItemsCount}
        tableParams={tableParams}
        onParamsChange={params => setTableParams(prev => ({ ...prev, ...params }))}
        enableSearch
      />
      {categoryToEdit && (
        <CategoryPopupForm
          category={categoryToEdit}
          afterAction={afterAction}
          handleServerError={handleServerError}
          handleClose={handleClose}
        />
      )}
      {categoryToDelete && (
        <ConfirmationDialog
          open
          message={t('pages.categories.confirmDelete')}
          handleYes={() => handleDelete(categoryToDelete)}
          handleNo={() => setCategoryToDelete(undefined)}
        />
      )}
      {!isBusy && paginationItemsCount === 0 && (
        <DefaultLandingMessage
          title={t('pages.categories.defaultLandingTitle')}
          body={t('pages.categories.defaultLandingMessage')}
          bodyLink={LINKS.categoriesKnowledgeBase}
        />
      )}
    </PageWithHeader>
  );
}

export default ManageCategories;
